"use client"

import {useCallback} from "react";
import {useQuery} from "@tanstack/react-query";
import {linkPreviewKeys} from "@/hooks/queries/queryKeys";
import {linkPreviewAPI} from "@/app/apiPaths";
import {
  ExternalLink,
  ExternalLinkPreview,
} from "@/pb/api/devplan/types/attachments";
import {
  GetExternalLinkPreviewRequest,
  GetExternalLinkPreviewResponse,
} from "@/pb/api/devplan/services/web/company";
import logger from "@/app/logger";

/**
 * Options for the useLinkPreview hook.
 */
export interface UseLinkPreviewOptions {
  /** The company ID for API authorization */
  companyId: number;
  /** The external link to fetch preview for */
  link: ExternalLink;
  /** Whether the query should be enabled (default: true) */
  enabled?: boolean;
}

/**
 * Return type for the useLinkPreview hook.
 */
export interface UseLinkPreviewResult {
  /** The fetched preview data, undefined if not loaded or on error */
  preview: ExternalLinkPreview | undefined;
  /** Whether the query is currently loading */
  isLoading: boolean;
  /** Any error that occurred (null on success or silent failure) */
  error: Error | null;
}

/**
 * React Query hook for fetching external link preview data.
 *
 * Features:
 * - Caches preview data for 5 minutes (staleTime)
 * - Silent failure: returns undefined preview on error (no UI error shown)
 * - Enabled only when link is provided and enabled=true
 *
 * @param options - Hook options including companyId, link, and enabled flag
 * @returns Object containing preview data, loading state, and error
 */
export function useLinkPreview(options: UseLinkPreviewOptions): UseLinkPreviewResult {
  const {companyId, link, enabled = true} = options;

  const queryFn = useCallback(async (): Promise<ExternalLinkPreview | null> => {
    logger.debug({companyId, provider: link.provider, identifier: link.identifier}, "Fetching link preview");

    const request: GetExternalLinkPreviewRequest = {link};

    const resp = await fetch(linkPreviewAPI(companyId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(GetExternalLinkPreviewRequest.toJSON(request)),
    });

    if (!resp.ok) {
      // Silent failure per requirements - log warning but return null
      logger.warn({companyId, provider: link.provider, identifier: link.identifier, status: resp.status}, "Failed to fetch link preview");
      return null;
    }

    const data = GetExternalLinkPreviewResponse.fromJSON(await resp.json());
    return data.preview ?? null;
  }, [companyId, link]);

  const queryKey = linkPreviewKeys.preview(companyId, link.provider, link.identifier, link.url);

  const {data, isPending: isLoading, error} = useQuery({
    queryKey,
    queryFn,
    enabled: enabled && !!link.identifier,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on failure (silent failure)
  });

  return {
    preview: data ?? undefined,
    isLoading,
    error: error as Error | null,
  };
}
